import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  Upload, Download, FileText, AlertCircle, CheckCircle, X, 
  RefreshCw, Users, Package, Wallet, FileSpreadsheet, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BulkUploadPage({ user, onLogout }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadType, setUploadType] = useState('employees');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const uploadTypes = [
    { value: 'employees', label: 'Employees', template: 'EMPLOYEE_BULK_UPLOAD_TEMPLATE.xlsx', icon: Users, color: '#2b3380' },
    { value: 'assets', label: 'Assets', template: 'ASSETS_BULK_UPLOAD_TEMPLATE.xlsx', icon: Package, color: '#4caf50' },
    { value: 'salary', label: 'Salary', template: 'SALARY_BULK_UPLOAD_TEMPLATE.xlsx', icon: Wallet, color: '#ff9800' }
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = (templateName) => {
    const link = document.createElement('a');
    link.href = `/templates/${templateName}`;
    link.download = templateName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/bulk/${uploadType}/bulk-upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      setShowResult(true);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      alert('Failed to upload file: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  // Stat Card Component (Zobox style)
  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const selectedTypeConfig = uploadTypes.find(t => t.value === uploadType);

  if (dataLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>Bulk Upload</h1>
            <p className="text-gray-500">Upload multiple records at once using Excel templates</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchDashboardStats}
              disabled={dataLoading}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Employees"
            value={dashboardStats?.total_employees || 0}
            subtitle="In system"
            icon={Users}
            color="#2b3380"
          />
          <StatCard
            title="Total Assets"
            value={dashboardStats?.total_assets || 0}
            subtitle="Registered"
            icon={Package}
            color="#4caf50"
          />
          <StatCard
            title="Templates"
            value={uploadTypes.length}
            subtitle="Available"
            icon={FileSpreadsheet}
            color="#ff9800"
          />
          <StatCard
            title="File Selected"
            value={selectedFile ? '1' : '0'}
            subtitle="Ready to upload"
            icon={Upload}
            color="#7e57c2"
          />
        </div>

        {/* Instructions Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-blue-50">
              <AlertCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">How to use Bulk Upload</h3>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Download the appropriate template for your data type</li>
                <li>Fill in the required information in the Excel file</li>
                <li>Make sure all required columns are filled</li>
                <li>Upload the completed file below</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Templates Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#2b338015' }}>
              <Download className="w-5 h-5" style={{ color: '#2b3380' }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#1a1f3a' }}>Download Templates</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {uploadTypes.map((type) => {
              const IconComponent = type.icon;
              return (
                <div 
                  key={type.value} 
                  className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-all hover:shadow-sm"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: `${type.color}15` }}>
                      <IconComponent className="w-5 h-5" style={{ color: type.color }} />
                    </div>
                    <h3 className="font-semibold text-gray-800">{type.label}</h3>
                  </div>
                  <button
                    onClick={() => handleDownloadTemplate(type.template)}
                    className="w-full px-4 py-2.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center justify-center gap-2 transition-colors border border-gray-200"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#2b338015' }}>
              <Upload className="w-5 h-5" style={{ color: '#2b3380' }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#1a1f3a' }}>Upload Data</h2>
          </div>
          
          {/* Upload Type Selection */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Data Type</label>
            <div className="grid grid-cols-3 gap-3">
              {uploadTypes.map((type) => {
                const IconComponent = type.icon;
                const isSelected = uploadType === type.value;
                return (
                  <button
                    key={type.value}
                    onClick={() => setUploadType(type.value)}
                    className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: isSelected ? `${type.color}20` : '#f3f4f6' }}
                    >
                      <IconComponent 
                        className="w-5 h-5" 
                        style={{ color: isSelected ? type.color : '#6b7280' }} 
                      />
                    </div>
                    <span className={`font-medium ${isSelected ? 'text-gray-800' : 'text-gray-600'}`}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select File</label>
            <div 
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              }`}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                {selectedFile ? (
                  <>
                    <div className="p-3 rounded-full bg-green-100 mb-3">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <span className="text-sm font-medium text-green-700 mb-1">{selectedFile.name}</span>
                    <span className="text-xs text-green-600">Click to change file</span>
                  </>
                ) : (
                  <>
                    <div className="p-3 rounded-full bg-gray-100 mb-3">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <span className="text-sm text-gray-600 mb-1">Click to select or drag and drop</span>
                    <span className="text-xs text-gray-500">Excel files only (.xlsx, .xls)</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full px-4 py-3 text-white rounded-xl flex items-center justify-center gap-2 font-medium transition-colors"
            style={{ backgroundColor: !selectedFile || uploading ? '#cccccc' : '#2b3380' }}
          >
            {uploading ? (
              <><Clock className="w-5 h-5 animate-spin" />Uploading...</>
            ) : (
              <><Upload className="w-5 h-5" />Upload {selectedTypeConfig?.label} File</>
            )}
          </button>
        </div>
      </div>

      {/* Result Modal */}
      {showResult && uploadResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-xl font-bold" style={{ color: '#1a1f3a' }}>Upload Result</h2>
              <button onClick={() => setShowResult(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600">Success</p>
                      <p className="text-2xl font-bold text-green-700">{uploadResult.success_count}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <X className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-red-600">Errors</p>
                      <p className="text-2xl font-bold text-red-700">{uploadResult.error_count}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Errors */}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Error Details:</h3>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-h-48 overflow-y-auto">
                    <ul className="text-sm text-red-700 space-y-1">
                      {uploadResult.errors.map((error, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">•</span>
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{uploadResult.message}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowResult(false)}
                className="px-5 py-2 text-white rounded-lg"
                style={{ backgroundColor: '#2b3380' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default BulkUploadPage;
